import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { getApiV1RanksCategories, type CategoryRankDto } from "@/client";
import { useSignalR } from "@/composables/signalr";
import type { CategoryRanksCreatedEvent } from "@/types/signalr-events";

export const useRanksStore = defineStore("ranks", () => {
  const dataLoading = ref(false);
  const dataLoaded = ref(false);
  const categoryRanks = ref<CategoryRankDto[]>([]);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (dataLoaded.value) {
      fetchRanks().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });

  async function fetchRanks() {
    dataLoading.value = true;
    try {
      // Fetch category ranks
      const response = await getApiV1RanksCategories();
      if (response.error) {
        console.error("Error fetching category ranks:", response.error);
        throw new Error("Failed to fetch category ranks");
      }
      const results = response.data;
      categoryRanks.value = results || [];

      dataLoaded.value = true;
    } finally {
      dataLoading.value = false;
    }
  }

  async function ensureRanksLoaded() {
    if (!dataLoaded.value) {
      await fetchRanks();
    }
  }

  subscribe<CategoryRanksCreatedEvent>(
    "CategoryRanksCreated",
    async (event) => {
      console.log("Received CategoryRanksCreated event:", event);
      // Replacing existing ranks of the same categoryId
      // If categoryId is not present, just add the new rank

      const categoryId = event.categoryId;
      const newRanks = event.categoryRanks;
      const existingRanks = categoryRanks.value.filter(
        (rank) => rank.categoryId !== categoryId,
      );
      categoryRanks.value = [...existingRanks, ...newRanks];
      console.log("Updated category ranks:", newRanks);
    },
  );

  return {
    // State
    categoryRanks,
    ranksLoaded: dataLoaded,
    ranksLoading: dataLoading,

    // Getters
    // (none yet)

    // Actions
    fetchRanks,
    ensureRanksLoaded,
  };
});
